/* ============================================================
   VITA AZZURRA — Storefront behaviour
   Vanilla JS. No dependencies. Progressive enhancement.
   ============================================================ */
(function () {
  'use strict';

  var CFG = window.VA || {};
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var $ = function (s, c) { return (c || document).querySelector(s); };
  var $$ = function (s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); };

  /* ---------- scroll lock ---------- */
  var lockCount = 0, savedY = 0;
  function lockScroll() {
    if (lockCount++ > 0) return;
    savedY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = -savedY + 'px';
    document.body.style.width = '100%';
  }
  function unlockScroll() {
    if (--lockCount > 0) return;
    lockCount = 0;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, savedY);
  }

  /* ---------- focus trap ---------- */
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  function trap(container, e) {
    if (e.key !== 'Tab') return;
    var items = $$(FOCUSABLE, container).filter(function (el) { return el.offsetParent !== null; });
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ---------- generic panel (menu + cart) ---------- */
  function Panel(el, overlay) {
    this.el = el;
    this.overlay = overlay;
    this.opener = null;
    this.isOpen = false;
    var self = this;
    this.onKey = function (e) {
      if (e.key === 'Escape') { self.close(); return; }
      trap(self.el, e);
    };
  }
  Panel.prototype.open = function (opener) {
    if (this.isOpen) return;
    this.isOpen = true;
    this.opener = opener || document.activeElement;
    this.el.classList.add('is-open');
    this.el.removeAttribute('aria-hidden');
    if (this.overlay) this.overlay.classList.add('is-open');
    lockScroll();
    document.addEventListener('keydown', this.onKey);
    var self = this;
    window.setTimeout(function () {
      var target = self.el.querySelector('[data-va-autofocus]') || $$(FOCUSABLE, self.el)[0];
      if (target) target.focus();
    }, 60);
  };
  Panel.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.el.classList.remove('is-open');
    this.el.setAttribute('aria-hidden', 'true');
    if (this.overlay) this.overlay.classList.remove('is-open');
    unlockScroll();
    document.removeEventListener('keydown', this.onKey);
    if (this.opener && document.contains(this.opener)) this.opener.focus();
  };

  /* ---------- reveal on scroll ----------
     Anything already inside the first viewport is revealed straight away,
     so above-the-fold copy can never stay invisible. */
  function initReveal(root) {
    var items = $$('[data-va-reveal]', root || document).filter(function (el) {
      return !el.classList.contains('is-revealed');
    });
    if (!items.length) return;
    if (reduced.matches || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.06 });

    var immediate = [];
    items.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) immediate.push(el);
      else io.observe(el);
    });
    if (immediate.length) {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          immediate.forEach(function (el) { el.classList.add('is-revealed'); });
        });
      });
    }
    // Safety net: nothing may stay hidden if something goes wrong.
    window.setTimeout(function () {
      items.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight * 1.2) el.classList.add('is-revealed');
      });
    }, 2500);
  }

  /* ---------- header ---------- */
  function initHeader() {
    var header = $('[data-va-header]');
    if (!header || header.dataset.vaBound) return;
    header.dataset.vaBound = '1';
    var overlay = header.classList.contains('is-overlay');
    var threshold = overlay ? Math.max(80, window.innerHeight * 0.14) : 8;
    var ticking = false;
    function update() {
      ticking = false;
      header.classList.toggle('is-solid', window.scrollY > threshold);
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
    window.addEventListener('resize', function () {
      threshold = overlay ? Math.max(80, window.innerHeight * 0.14) : 8;
      update();
    }, { passive: true });
    update();
  }

  /* ---------- mobile menu ---------- */
  var menuPanel = null;
  function initMenu() {
    var menu = $('[data-va-menu]');
    if (!menu || menu.dataset.vaBound) return;
    menu.dataset.vaBound = '1';
    menuPanel = new Panel(menu, null);
    $$('[data-va-menu-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        menuPanel.open(btn);
        btn.setAttribute('aria-expanded', 'true');
      });
    });
    $$('[data-va-menu-close]', menu).forEach(function (btn) {
      btn.addEventListener('click', function () { closeMenu(); });
    });
    $$('a', menu).forEach(function (a) {
      a.addEventListener('click', function () { closeMenu(); });
    });
    function closeMenu() {
      menuPanel.close();
      $$('[data-va-menu-open]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    }
    var mq = window.matchMedia('(min-width: 64em)');
    var onChange = function (e) { if (e.matches) closeMenu(); };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
  }

  /* ---------- cart drawer ---------- */
  var cartPanel = null;
  function getCartPanel() {
    var drawer = $('[data-va-cart-drawer]');
    var overlay = $('[data-va-overlay]');
    if (!drawer) return null;
    if (!cartPanel || cartPanel.el !== drawer) cartPanel = new Panel(drawer, overlay);
    return cartPanel;
  }
  function bindCartUI() {
    var panel = getCartPanel();
    if (!panel) return;
    $$('[data-va-cart-close]').forEach(function (btn) {
      if (btn.dataset.vaBound) return;
      btn.dataset.vaBound = '1';
      btn.addEventListener('click', function () { panel.close(); });
    });
    var overlay = $('[data-va-overlay]');
    if (overlay && !overlay.dataset.vaBound) {
      overlay.dataset.vaBound = '1';
      overlay.addEventListener('click', function () { getCartPanel().close(); });
    }
    $$('[data-va-qty]').forEach(function (wrap) {
      if (wrap.dataset.vaBound) return;
      wrap.dataset.vaBound = '1';
      var input = $('input', wrap);
      if (!input) return;
      $$('button', wrap).forEach(function (btn) {
        btn.addEventListener('click', function () {
          var step = btn.dataset.vaQtyStep === 'up' ? 1 : -1;
          var next = Math.max(0, (parseInt(input.value, 10) || 0) + step);
          changeLine(input.dataset.vaLineKey, next);
        });
      });
      input.addEventListener('change', function () {
        changeLine(input.dataset.vaLineKey, Math.max(0, parseInt(input.value, 10) || 0));
      });
    });
    $$('[data-va-cart-remove]').forEach(function (btn) {
      if (btn.dataset.vaBound) return;
      btn.dataset.vaBound = '1';
      btn.addEventListener('click', function () { changeLine(btn.dataset.vaCartRemove, 0); });
    });
  }

  function setBusy(state) {
    var drawer = $('[data-va-cart-drawer]');
    if (drawer) drawer.setAttribute('aria-busy', state ? 'true' : 'false');
  }

  function applySections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach(function (id) {
      var html = sections[id];
      if (typeof html !== 'string') return;
      var target = document.getElementById('shopify-section-' + id);
      if (!target) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.getElementById('shopify-section-' + id);
      if (fresh) target.innerHTML = fresh.innerHTML;
    });
    cartPanel = null;
    bindCartUI();
    initAccordions();
    updateCartCounts();
  }

  function updateCartCounts() {
    if (!CFG.routes) return;
    fetch(CFG.routes.cart + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        $$('[data-va-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
      })
      .catch(function () {});
  }

  function changeLine(key, quantity) {
    if (!key || !CFG.routes) return;
    setBusy(true);
    fetch(CFG.routes.cart_change + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity, sections: 'va-cart-drawer' })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var panel = getCartPanel();
        var wasOpen = panel && panel.isOpen;
        applySections(data.sections);
        if (wasOpen) { var p = getCartPanel(); if (p) { p.isOpen = false; p.open(); } }
      })
      .catch(function () {})
      .then(function () { setBusy(false); });
  }

  function initAddToCart() {
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-va-add-form]');
      if (!form || !CFG.routes) return;
      e.preventDefault();
      var btn = form.querySelector('[type="submit"]');
      var errorEl = form.querySelector('[data-va-form-error]');
      if (errorEl) errorEl.textContent = '';
      if (btn) { btn.setAttribute('aria-disabled', 'true'); btn.dataset.vaLabel = btn.textContent; btn.textContent = CFG.strings.adding; }
      var data = new FormData(form);
      data.append('sections', 'va-cart-drawer');
      fetch(CFG.routes.cart_add + '.js', { method: 'POST', headers: { Accept: 'application/json' }, body: data })
        .then(function (r) { return r.json().then(function (json) { return { ok: r.ok, json: json }; }); })
        .then(function (res) {
          if (!res.ok) {
            if (errorEl) errorEl.textContent = res.json.description || res.json.message || CFG.strings.error;
            return;
          }
          applySections(res.json.sections);
          var panel = getCartPanel();
          if (panel) panel.open(btn);
        })
        .catch(function () { if (errorEl) errorEl.textContent = CFG.strings.error; })
        .then(function () {
          if (btn) { btn.removeAttribute('aria-disabled'); btn.textContent = btn.dataset.vaLabel || btn.textContent; }
        });
    });

    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-va-cart-open]');
      if (!opener) return;
      var panel = getCartPanel();
      if (!panel) return;
      e.preventDefault();
      panel.open(opener);
    });
  }

  /* ---------- accordions ---------- */
  function initAccordions() {
    $$('[data-va-acc-btn]').forEach(function (btn) {
      if (btn.dataset.vaBound) return;
      btn.dataset.vaBound = '1';
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;
      var inner = panel.firstElementChild;
      var open = btn.getAttribute('aria-expanded') === 'true';
      panel.style.height = open ? 'auto' : '0px';
      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (reduced.matches) { panel.style.height = expanded ? '0px' : 'auto'; return; }
        if (expanded) {
          panel.style.height = inner.offsetHeight + 'px';
          window.requestAnimationFrame(function () {
            window.requestAnimationFrame(function () { panel.style.height = '0px'; });
          });
        } else {
          panel.style.height = inner.offsetHeight + 'px';
          var done = function () {
            panel.style.height = 'auto';
            panel.removeEventListener('transitionend', done);
          };
          panel.addEventListener('transitionend', done);
        }
      });
    });
  }

  /* ---------- PDP gallery dots ---------- */
  function initGallery() {
    var gallery = $('[data-va-gallery]');
    var dots = $$('[data-va-gallery-dot]');
    if (!gallery || !dots.length || gallery.dataset.vaBound) return;
    gallery.dataset.vaBound = '1';
    var slides = $$('[data-va-gallery-slide]', gallery);
    function sync() {
      if (gallery.scrollWidth <= gallery.clientWidth + 4) return;
      var idx = 0, best = Infinity;
      var mid = gallery.scrollLeft + gallery.clientWidth / 2;
      slides.forEach(function (s, i) {
        var c = s.offsetLeft + s.offsetWidth / 2;
        var d = Math.abs(c - mid);
        if (d < best) { best = d; idx = i; }
      });
      dots.forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
    }
    gallery.addEventListener('scroll', function () { window.requestAnimationFrame(sync); }, { passive: true });
    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        if (slides[i]) slides[i].scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
      });
    });
    sync();
  }

  /* ---------- sticky buy bar ---------- */
  function initBuyBar() {
    var bar = $('[data-va-buybar]');
    var anchor = $('[data-va-buybar-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    }, { threshold: 0 });
    io.observe(anchor);
  }

  /* ---------- parallax (large screens only, opt-in) ---------- */
  function initParallax() {
    var items = $$('[data-va-parallax]');
    if (!items.length || reduced.matches || window.innerWidth < 1024) return;
    var ticking = false;
    function update() {
      ticking = false;
      var vh = window.innerHeight;
      items.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        var progress = (rect.top + rect.height / 2 - vh / 2) / vh;
        var strength = parseFloat(el.dataset.vaParallax) || 6;
        el.style.transform = 'translate3d(0,' + (-progress * strength).toFixed(2) + '%,0)';
      });
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
    update();
  }

  /* ---------- init ---------- */
  function init() {
    initReveal();
    initHeader();
    initMenu();
    bindCartUI();
    initAddToCart();
    initAccordions();
    initGallery();
    initBuyBar();
    initParallax();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.addEventListener('load', function () { initReveal(); });

  /* Theme editor support */
  document.addEventListener('shopify:section:load', function (e) {
    initReveal(e.target);
    initAccordions();
    bindCartUI();
    initGallery();
    initHeader();
    initMenu();
  });
  document.addEventListener('shopify:section:select', function () { initReveal(); });
})();
